/**
 * EditVOModal Component
 * Modal for editing a variation order
 */

import React, { useState, useEffect } from 'react';
import { variationOrderAPI } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import { toast } from 'react-toastify';

const EditVOModal = ({ isOpen, onClose, onSuccess, vo, projectId }) => {
  const [formData, setFormData] = useState({
    description: '',
    amount: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Check if VO has payments (cannot delete if has payments)
  const hasPayments = (vo?.paymentRecords?.length || 0) > 0;
  const actualPaid = parseFloat(vo?.actualPaidAmount) || 0;

  useEffect(() => {
    if (vo) {
      setFormData({
        description: vo.description || '',
        amount: vo.amount?.toString() || ''
      });
    }
  }, [vo]);

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    // Warn if new amount is less than already paid
    if (parseFloat(formData.amount) < actualPaid) {
      toast.error(`Amount cannot be less than already paid amount (${formatCurrency(actualPaid)})`);
      return;
    }

    setSubmitting(true);

    try {
      await variationOrderAPI.update(projectId, vo.id, {
        description: formData.description,
        amount: parseFloat(formData.amount)
      });

      toast.success('Variation order updated successfully');
      onSuccess();
    } catch (err) {
      console.error('Error updating VO:', err);
      toast.error(err.message || 'Failed to update variation order');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    setSubmitting(true);

    try {
      await variationOrderAPI.delete(projectId, vo.id);
      toast.success('Variation order deleted successfully');
      onSuccess();
    } catch (err) {
      console.error('Error deleting VO:', err);
      toast.error(err.message || 'Failed to delete variation order');
    } finally {
      setSubmitting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-75 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Edit Variation Order
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">{vo?.voNumber}</p>
          </div>

          {/* Content */}
          <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1">
            {/* Payment Info */}
            {actualPaid > 0 && (
              <div className="bg-green-50 rounded-lg p-3 mb-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Already Paid:</span>
                  <span className="font-medium text-green-600">{formatCurrency(actualPaid)}</span>
                </div>
              </div>
            )}

            {/* Form Fields */}
            <div className="space-y-4">
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  disabled={submitting}
                  rows={3}
                  placeholder="Describe the variation order..."
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 text-base touch-manipulation"
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (RM) *
                </label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  disabled={submitting}
                  min={actualPaid > 0 ? actualPaid : 0}
                  step="0.01"
                  placeholder="Enter VO amount"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                />
                {actualPaid > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Amount cannot be less than already paid ({formatCurrency(actualPaid)})
                  </p>
                )}
              </div>

              {/* Files Info (read-only) */}
              {(vo?.quotationFiles?.length > 0 || vo?.invoiceFiles?.length > 0) && (
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm text-gray-500 mb-2">Attached Files</p>
                  {vo?.quotationFiles?.length > 0 && (
                    <p className="text-sm text-gray-700">
                      Quotations: {vo.quotationFiles.length} file(s)
                    </p>
                  )}
                  {vo?.invoiceFiles?.length > 0 && (
                    <p className="text-sm text-gray-700">
                      Invoices: {vo.invoiceFiles.length} file(s)
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Delete Section */}
            {!hasPayments && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                {!showDeleteConfirm ? (
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={submitting}
                    className="text-red-600 hover:text-red-700 text-sm font-medium min-h-[44px] touch-manipulation"
                  >
                    Delete this variation order
                  </button>
                ) : (
                  <div className="bg-red-50 rounded-lg p-3">
                    <p className="text-sm text-red-700 mb-3">
                      Are you sure you want to delete this variation order? This cannot be undone.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={submitting}
                        className="px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 min-h-[44px] touch-manipulation"
                      >
                        {submitting ? 'Deleting...' : 'Yes, Delete'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={submitting}
                        className="px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 min-h-[44px] touch-manipulation"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {hasPayments && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  This VO has payment records and cannot be deleted.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="w-full sm:w-auto px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditVOModal;
